import React, { forwardRef } from 'react';
import { StyledVideoStage, StyledVideoFrame, StyledVideo } from './index.style';
import CaptionOverlay from './CaptionOverlay';

/**
 * DOM-based preview stage: the original <video> inside the aspect-ratio frame.
 * Extracted from VideoPreviewPanel so a canvas-based stage (feature flag
 * `video-dubbing-canvas-preview`) can swap in without touching the function
 * bar (aspect ratio / background colour / expand controls).
 */
const VideoStage = forwardRef(
  (
    {
      videoUrl,
      // Ratio state computed once by the panel and shared with the canvas
      // stage; only the letterbox CSS below is DOM-stage-specific.
      selectedRatio,
      aspectRatioCssValue,
      videoNaturalRatio,
      onVideoMetadata,
      backgroundColor,
      showBackgroundOnly,
      // Feature flag `video-dubbing-caption`: the current block’s caption on a
      // transparent canvas over the frame. The props below only give it a clock.
      captionOverlayEnabled = false,
      isPlaying = false,
      getCurrentSec,
      currentSec = 0,
    },
    videoRef,
  ) => {
    // Past the end of the video there is no frame to letterbox, so the frame
    // falls back to the video's own aspect ratio: that keeps the coloured area
    // exactly the size the video occupied (and the size the export renders).
    // Ratio still unknown (metadata not loaded) → leave the preview as it is.
    // With no ratio picked a box is still needed past the end of the video and
    // for the caption canvas to anchor to. Both use the video’s own ratio.
    const needsFrame = showBackgroundOnly || captionOverlayEnabled;
    const frameRatioCssValue =
      aspectRatioCssValue ||
      (needsFrame && videoNaturalRatio ? `${videoNaturalRatio}` : null);
    const hasFrame = Boolean(frameRatioCssValue);
    const backgroundOnly = showBackgroundOnly && hasFrame;
    // At the source ratio the box matches the video, so there is nothing to fill.
    const aspectsMatch =
      selectedRatio == null ||
      (videoNaturalRatio != null &&
        Math.abs(selectedRatio - videoNaturalRatio) / selectedRatio < 0.005);
    // The frame paints the background either to letterbox a mismatched ratio or
    // to stand in for the missing frame past the end of the video.
    const showBackground = backgroundOnly || (hasFrame && !aspectsMatch);

    return (
      <StyledVideoStage>
        <StyledVideoFrame
          aspectRatioValue={frameRatioCssValue}
          showBackground={showBackground}
          backgroundColor={backgroundColor}
          hasOverlay={captionOverlayEnabled}
        >
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <StyledVideo
            ref={videoRef}
            src={videoUrl}
            // Same request mode as the timeline's thumbnail extractor, so S3
            // caches one CORS-enabled (ACAO) copy of this url for both. A
            // no-CORS load here would cache an ACAO-less copy that then
            // CORS-blocks the crossOrigin thumbnail request for the same url.
            crossOrigin="anonymous"
            preload="auto"
            playsInline
            hasFrame={hasFrame}
            isHidden={backgroundOnly}
            onLoadedMetadata={onVideoMetadata}
          />
          {captionOverlayEnabled && hasFrame && (
            <CaptionOverlay
              isPlaying={isPlaying}
              getCurrentSec={getCurrentSec}
              currentSec={currentSec}
            />
          )}
        </StyledVideoFrame>
      </StyledVideoStage>
    );
  },
);

export default VideoStage;
